import { Component, Inject, OnInit, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  NabDirectPostPayload,
  NabTransactPaymentRequest,
  NabTransactPaymentResponse,
  PreparePaymentResponse,
  ProcessPaymentResponse,
  ProjectSubscription,
  SubscriptionInvoice,
  SubscriptionPricingConfig,
  SubscriptionSeatUsage,
  SubscriptionStatus,
  SUBSCRIPTION_CONSTANTS
} from '../../models/subscription.model';
import { NabTransactService } from '../../services/nab-transact.service';
import { SubscriptionService } from '../../services/subscription.service';
import { UserService } from '../../services/user.service';
import { BackendApiService } from '../../services/backend-api.service';
import { ApiConfigService } from '../../services/api-config.service';

@Component({
  selector: 'app-subscription-dialog',
  templateUrl: './subscription-dialog.component.html',
  styleUrls: ['./subscription-dialog.component.scss'],
  standalone: false
})
export class SubscriptionDialogComponent implements OnInit {

  readonly CONSTANTS = SUBSCRIPTION_CONSTANTS;
  readonly Math = Math;

  subscription!: ProjectSubscription;
  seatUsage!: SubscriptionSeatUsage;
  invoices: SubscriptionInvoice[] = [];

  selectedTabIndex = 0;
  selectedUnits = 1;

  // Direct Post v2 State & Security
  prepareData: PreparePaymentResponse | null = null;
  agreePayments = false;

  // Form Fields
  cardNumber = '';
  cardholderName = '';
  expiryMonth = '12';
  expiryYear = '2028';
  cvv = '';
  postcode = '3000';
  email = '';
  company = '';

  isProcessing = false;
  directPostResult: ProcessPaymentResponse | null = null;
  paymentResult: NabTransactPaymentResponse | null = null;
  paymentError: string | null = null;

  readonly months = [
    '01', '02', '03', '04', '05', '06',
    '07', '08', '09', '10', '11', '12'
  ];

  readonly years = ['2026', '2027', '2028', '2029', '2030', '2031', '2032'];

  constructor(
    @Optional() public dialogRef: MatDialogRef<SubscriptionDialogComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: { initialTab?: number },
    public subService: SubscriptionService,
    public userService: UserService,
    private nabService: NabTransactService,
    private backendApi: BackendApiService,
    public apiConfig: ApiConfigService,
    private snackBar: MatSnackBar
  ) {
    if (data?.initialTab != null) {
      this.selectedTabIndex = data.initialTab;
    }
  }

  ngOnInit(): void {
    this.subService.subscription$.subscribe(sub => {
      this.subscription = sub;
      if (this.selectedUnits < sub.units) {
        this.selectedUnits = sub.units;
      }
    });

    this.subService.seatUsage$.subscribe(usage => {
      this.seatUsage = usage;
      if (usage.unitsRequired > this.selectedUnits) {
        this.selectedUnits = usage.unitsRequired;
      }
    });

    this.subService.invoices$.subscribe(invs => {
      this.invoices = invs;
    });

    const current = this.userService.getCurrentUser();
    this.cardholderName = current?.displayName || 'Mark Taylor';
    this.email = current?.email || 'owner@sprinta.io';
    this.company = 'Sprinta Project';
  }

  get isOwner(): boolean {
    return this.userService.isOwner();
  }

  get pricing(): SubscriptionPricingConfig {
    return this.subService.pricing;
  }

  get totalAnnualPrice(): number {
    return this.selectedUnits * this.pricing.annualPriceAud;
  }

  get totalMonthlyPrice(): number {
    return this.selectedUnits * this.pricing.monthlyPriceAud;
  }

  get detectedCardBrand(): string {
    return this.nabService.detectCardBrand(this.cardNumber);
  }

  get ownerCapacity(): number {
    return this.selectedUnits * SUBSCRIPTION_CONSTANTS.OWNER_SEATS_PER_UNIT;
  }

  get developerCapacity(): number {
    return this.selectedUnits * this.pricing.seatsPerUnit.developers;
  }

  get stakeholderCapacity(): number {
    return this.selectedUnits * SUBSCRIPTION_CONSTANTS.STAKEHOLDER_SEATS_PER_UNIT;
  }

  adjustUnits(delta: number): void {
    const updated = this.selectedUnits + delta;
    if (updated >= 1 && updated <= 50) {
      this.selectedUnits = updated;
    }
  }

  goToPayment(): void {
    this.selectedTabIndex = 1;
    this.directPostResult = null;
    this.paymentResult = null;
    this.paymentError = null;
  }

  fillTestCard(type: 'approved' | 'insufficient' | 'expired'): void {
    this.cardholderName = 'Mark Taylor';
    this.expiryMonth = '12';
    this.expiryYear = '2028';
    this.cvv = '456';
    this.postcode = '3000';
    this.agreePayments = true;

    if (type === 'approved') {
      this.cardNumber = '4564 0000 0000 0001';
    } else if (type === 'insufficient') {
      this.cardNumber = '4564 0000 0000 0002';
    } else if (type === 'expired') {
      this.cardNumber = '4564 0000 0000 0003';
      this.expiryYear = '2024';
    }
    this.paymentError = null;
    this.paymentResult = null;
    this.directPostResult = null;
  }

  onCardNumberInput(event: any): void {
    let value = (event.target.value || '').replace(/\D/g, '');
    if (value.length > 16) {
      value = value.substring(0, 16);
    }
    const formatted = value.replace(/(.{4})/g, '$1 ').trim();
    this.cardNumber = formatted;
  }

  /**
   * Payment submission:
   * Fingerprint is requested immediately before payment and bound to the pending payment attempt.
   * Subscription is activated only upon server-side verification.
   */
  processPayment(): void {
    if (!this.agreePayments) {
      this.paymentError = 'Please agree to the NAB Transact secure processing terms to proceed.';
      return;
    }

    if (!this.cardNumber || !this.cardholderName || !this.cvv) {
      this.paymentError = 'Please complete all credit card fields.';
      return;
    }

    this.isProcessing = true;
    this.paymentError = null;
    this.directPostResult = null;
    this.paymentResult = null;

    const rawDigits = this.cardNumber.replace(/\D/g, '');
    const cardholder = this.cardholderName;
    const expMonth = this.expiryMonth;
    const expYear = this.expiryYear;
    const cleanCvv = this.cvv;
    const projectId = this.subscription?.projectId || 'proj-sprinta-alpha';

    // Client Sanitization: clear raw card digits from component memory before post
    this.cardNumber = '';

    if (this.apiConfig.isLiveMode) {
      // LIVE API MODE:
      // Step 1: Request preparation immediately before payment (binds pending payment attempt on server)
      this.backendApi.prepareDirectPost(projectId, {
        unitsPurchased: this.selectedUnits,
        currency: 'AUD'
      }).subscribe({
        next: (prep) => {
          this.prepareData = prep;
          const payload: NabDirectPostPayload = {
            EPS_MERCHANT: prep.merchantId,
            EPS_TXNTYPE: prep.transactionType || '0',
            EPS_REFERENCEID: prep.referenceId,
            EPS_AMOUNT: prep.amount,
            EPS_TIMESTAMP: prep.timestamp,
            EPS_FINGERPRINT: prep.fingerprint,
            EPS_RESULTURL: 'https://app.sprinta.io/payment/complete',
            EPS_CARDNUMBER: rawDigits,
            EPS_CARDNAME: cardholder,
            EPS_EXPIRYMONTH: expMonth,
            EPS_EXPIRYYEAR: expYear,
            EPS_CCV: cleanCvv
          };

          // Step 2: Direct Post to NAB Transact
          this.nabService.processDirectPost(payload).subscribe({
            next: (directRes) => {
              // Step 3: Submit transaction result to backend for server-side verification
              this.backendApi.submitPaymentResult(projectId, {
                paymentId: prep.paymentId,
                referenceId: prep.referenceId,
                confirmation: directRes.confirmation,
                transactionId: directRes.details?.transactionId || '',
                maskedPan: directRes.details?.payment?.pan || '',
                timestamp: new Date().toISOString()
              }).subscribe({
                next: (verifyRes) => {
                  this.isProcessing = false;
                  if (verifyRes.verified) {
                    this.directPostResult = directRes;
                    this.subService.refreshSeatUsage();
                    this.snackBar.open(
                      `Payment verified and subscription activated for ${this.selectedUnits} unit(s)!`,
                      'Close',
                      { duration: 5000 }
                    );
                  } else {
                    this.paymentError = verifyRes.message || 'Payment verification failed.';
                  }
                },
                error: (verifyErr) => {
                  this.isProcessing = false;
                  if (verifyErr.status === 403) {
                    this.paymentError = 'Subscription modification blocked (403 SUBSCRIPTION_BLOCKED).';
                  } else if (verifyErr.status === 402) {
                    this.paymentError = 'Payment required or declined by banking network (402 PAYMENT_REQUIRED).';
                  } else {
                    if (verifyErr.status === 501 || verifyErr?.error?.error?.code === 'PAYMENT_PROVIDER_NOT_CONFIGURED') {
                    this.paymentError = 'Payment Provider Integration Pending: The backend API returned 501 PAYMENT_PROVIDER_NOT_CONFIGURED. Subscriptions are active in initial launch mode.';
                  } else {
                    this.paymentError = 'Unable to verify payment with Sprinta Cloud API. Please check your network connection.';
                  }
                  }
                }
              });
            },
            error: (directErr) => {
              this.isProcessing = false;
              this.paymentError = this.nabService.lookupErrorMessage(directErr?.message || '');
            }
          });
        },
        error: (prepErr) => {
          this.isProcessing = false;
          if (prepErr.status === 403) {
            this.paymentError = 'Action blocked: Subscription is currently blocked from modifications (403 SUBSCRIPTION_BLOCKED).';
          } else if (prepErr.status === 402) {
            this.paymentError = 'Payment declined or payment required (402 PAYMENT_REQUIRED).';
          } else {
            if (prepErr.status === 501 || prepErr?.error?.error?.code === 'PAYMENT_PROVIDER_NOT_CONFIGURED') {
            this.paymentError = 'Payment Provider Integration Pending: The backend API returned 501 PAYMENT_PROVIDER_NOT_CONFIGURED. Subscriptions are active in initial launch mode. Test credit card processing is available in Demo Mode.';
          } else {
            this.paymentError = 'Sprinta Cloud API is offline or unreachable. Unable to prepare payment.';
          }
          }
        }
      });
    } else {
      // DEMO / MOCK MODE:
      // Immediate prepare & simulated direct post
      this.nabService.preparePayment(
        this.totalAnnualPrice,
        'SPRINTA-REF-' + Date.now(),
        `Annual Sprinta Subscription (${this.selectedUnits} Unit${this.selectedUnits > 1 ? 's' : ''})`
      ).subscribe({
        next: (data) => {
          this.prepareData = data;
          const payload: NabDirectPostPayload = {
            EPS_MERCHANT: data.merchantId,
            EPS_TXNTYPE: '0',
            EPS_REFERENCEID: data.referenceId,
            EPS_AMOUNT: this.totalAnnualPrice.toFixed(2),
            EPS_TIMESTAMP: data.timestamp,
            EPS_FINGERPRINT: data.fingerprint,
            EPS_RESULTURL: 'https://app.sprinta.io/payment/complete',
            EPS_CARDNUMBER: rawDigits,
            EPS_CARDNAME: cardholder,
            EPS_EXPIRYMONTH: expMonth,
            EPS_EXPIRYYEAR: expYear,
            EPS_CCV: cleanCvv,
            firstName: cardholder.split(' ')[0] || 'Sprinta',
            lastName: cardholder.split(' ').slice(1).join(' ') || 'User',
            email: this.email,
            company: this.company,
            itemName: `Annual Sprinta Subscription (${this.selectedUnits} Unit${this.selectedUnits > 1 ? 's' : ''})`
          };

          this.subService.purchaseOrRenewDirectPost(this.selectedUnits, payload).subscribe({
            next: (res: ProcessPaymentResponse) => {
              this.isProcessing = false;
              if (res.confirmation) {
                this.directPostResult = res;
                this.paymentError = null;
                this.snackBar.open(
                  `Payment of $${this.totalAnnualPrice} AUD processed successfully via NAB Transact Direct Post v2!`,
                  'Close',
                  { duration: 5000 }
                );
              } else {
                this.paymentError = this.nabService.lookupErrorMessage(res.message);
              }
            },
            error: (err: any) => {
              this.isProcessing = false;
              this.paymentError = this.nabService.lookupErrorMessage(err?.message || '');
            }
          });
        },
        error: (err) => {
          this.isProcessing = false;
          this.paymentError = 'Unable to prepare payment in demo mode.';
        }
      });
    }
  }

  close(): void {
    if (this.dialogRef) {
      this.dialogRef.close(true);
    }
  }
}
